import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Card,
  FieldsValues,
  FormValues,
  PreparedServerCard,
  SavedCard,
  ServerCard,
} from '../types';
import {
  checkIsSavedCard,
  checkIsVirtualCard,
  getActiveCardData,
  getAllowedSavedCardsByActiveCard,
  prepareCards,
} from '../helpers';
import { useTouchedFields } from '../hooks/useTouchedFields';
import { defaultValues, fieldsNames } from '../constants';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';

type ContextValues = {
  activeCard: Card;
  cardToDisplay: FormValues;

  isSavedCard: boolean;
  isVirtualCard: boolean;

  cards: PreparedServerCard[];

  checkIfFieldInvalid: (name: FieldsValues) => boolean;

  selectSavedCard: (card: PreparedServerCard) => void;
  selectNewCard: VoidFunction;
  changeFieldValue: (name: FieldsValues, value: string | boolean) => void;

  touchField: (name: FieldsValues) => void;
  focusFieldByName: (name: FieldsValues) => void;

  isSubmitted: boolean;

  creditCardRef: React.RefObject<HTMLDivElement>;
};

const Context = React.createContext<ContextValues>({
  activeCard: {} as Card,
  cardToDisplay: {} as FormValues,
  isSavedCard: false,
  isVirtualCard: false,
  cards: [],
  checkIfFieldInvalid: () => false,
  selectSavedCard: noop,
  selectNewCard: noop,
  changeFieldValue: noop,
  touchField: noop,
  focusFieldByName: noop,
  isSubmitted: false,

  creditCardRef: { current: null },
});

type Props = {
  activeCard: Card;
  setActiveCard: (card: Card) => void;
  savedCards: ServerCard[];
  errors: Partial<Record<FieldsValues, string>>;
  isSubmitted: boolean;
};

export const useCreditCardContext = () => {
  return useContext<ContextValues>(Context);
};

export const CreditCardContext = (props: PropsWithChildren<Props>) => {
  const {
    children,
    activeCard,
    setActiveCard,
    savedCards,
    errors,
    isSubmitted,
  }: PropsWithChildren<Props> = props;

  const creditCardRef = useRef<HTMLDivElement>(null);

  const preparedSavedCards = useMemo(() => prepareCards(savedCards), [savedCards]);

  const cardToDisplay = useMemo(
    () => getActiveCardData(activeCard, preparedSavedCards),
    [activeCard, preparedSavedCards]
  );

  const isSavedCard = checkIsSavedCard(activeCard);
  const isVirtualCard = checkIsVirtualCard(activeCard, preparedSavedCards);
  const { touchedFields, touchField, resetTouchedFields } = useTouchedFields();

  const checkIfFieldInvalid = useCallback(
    (name: FieldsValues): boolean => {
      const canShowError = isSubmitted || touchedFields[name];

      return Boolean(canShowError && errors[name]);
    },
    [isSubmitted, touchedFields, errors]
  );

  const selectSavedCard = useCallback(
    (card: PreparedServerCard) => {
      setActiveCard({
        [fieldsNames.id]: card.id,
        [fieldsNames.cvv]: '',
      });

      resetTouchedFields();
    },
    [setActiveCard, resetTouchedFields]
  );

  const selectNewCard = useCallback(() => {
    setActiveCard(defaultValues);
    resetTouchedFields();
  }, [setActiveCard, resetTouchedFields]);

  const changeFieldValue = useCallback(
    (name: FieldsValues, value: string | boolean) => {
      setActiveCard({
        ...activeCard,
        [name]: value,
      });
    },
    [activeCard, setActiveCard]
  );

  const [filteredByOTTFlagCards, setFilteredByOTTFlagCards] = useState<PreparedServerCard[]>([]);

  useEffect(() => {
    const newFilteredCards = getAllowedSavedCardsByActiveCard(activeCard, preparedSavedCards);

    const currentFilteredCardsIDs = filteredByOTTFlagCards.map((card) => card.id);
    const newFilteredCardsIDs = newFilteredCards.map((card) => card.id);
    if (!isEqual(currentFilteredCardsIDs, newFilteredCardsIDs)) {
      setFilteredByOTTFlagCards(newFilteredCards);

      if (!(activeCard as SavedCard).id && newFilteredCards.length) {
        selectSavedCard(newFilteredCards[0]);
      }
    }
  }, [preparedSavedCards, selectSavedCard, filteredByOTTFlagCards, activeCard]);

  const focusFieldByName = useCallback((name: FieldsValues) => {
    if (creditCardRef?.current) {
      const field: HTMLInputElement | null = creditCardRef?.current.querySelector(
        `input[name="${name}"]`
      );
      field?.focus();
    }
  }, []);

  const contextValues = useMemo(
    () => ({
      activeCard,
      cardToDisplay,
      isSavedCard,
      isVirtualCard,
      cards: filteredByOTTFlagCards,
      checkIfFieldInvalid,
      selectSavedCard,
      selectNewCard,
      changeFieldValue,
      touchField,
      focusFieldByName,
      isSubmitted,
      creditCardRef,
    }),
    [
      activeCard,
      cardToDisplay,
      isSavedCard,
      isVirtualCard,
      filteredByOTTFlagCards,
      checkIfFieldInvalid,
      selectSavedCard,
      selectNewCard,
      changeFieldValue,
      touchField,
      focusFieldByName,
      isSubmitted,
    ]
  );

  return <Context.Provider value={contextValues}>{children}</Context.Provider>;
};
